/**
 * @file CardBorderFields.tsx
 * @description Рамка и тень карточек числами (phase19.16, партия SB120,
 * этап 2; проект uiux-sb120-stage2 §2 «Карточки и фото», §10 вопрос 2 —
 * тень в % по шкале этапа 1; данные db-design-sb120-stage2 §1
 * `cards {border {width 0–4, color?}, shadow 0–100}`):
 *
 * ```
 * Рамка: толщина ├●──────┤ [ 1 ] px   [Нет 0][1][2]
 *        цвет    [Авто: линия ✓][Фон страницы]…[Кнопки]…[⌖ Свой]
 * Тень           ├●──────┤ [ 0 ] %    [Нет 0][Как было 50][Плотная 100]
 * ```
 *
 * Цвет рамки — только при толщине от 1 px; нет ключа — линия сайта.
 * Часть `CardLookFields` (общих для темы и «Своей» карточки товара).
 */

import { SITE_THEME_LIMITS, type SiteCards, type SiteColors } from "@chaiberi/shared";

import { FieldHint } from "../../ui/settings-groups";
import { ColorRefField } from "../../site-fields/ColorRefField";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { BORDER_WIDTH_PRESETS, CARD_SHADOW_PRESETS } from "../../../lib/site-view/appearance-numbers";

const { cardBorderWidth: BW, cardShadow: CS } = SITE_THEME_LIMITS;

interface Props {
  cards: SiteCards;
  colors: SiteColors;
  /** Чья рамка — в подписях для чтеца экрана: «карточек», «карточки товара». */
  of: string;
  onChange: (cards: SiteCards) => void;
  onPreview?: (cards: SiteCards) => void;
}

export function CardBorderFields({ cards, colors, of, onChange, onPreview }: Props) {
  const border = (patch: Partial<SiteCards["border"]>): SiteCards => {
    const next = { ...cards.border, ...patch };
    // Нет цвета — линия сайта: ключ снимается.
    if (next.color === undefined) delete next.color;
    return { ...cards, border: next };
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <NumberSliderField
        label={`Рамка ${of}: толщина`}
        value={cards.border.width}
        inherit={{ value: 0, label: "без рамки" }}
        min={BW.min}
        max={BW.max}
        step={1}
        integer
        unit="px"
        presets={BORDER_WIDTH_PRESETS}
        onChange={(v) => onChange(border({ width: v ?? 0 }))}
        onPreview={onPreview ? (v) => onPreview(border({ width: v ?? 0 })) : undefined}
      />
      {cards.border.width > 0 ? (
        <ColorRefField
          label={`Цвет рамки ${of}`}
          value={cards.border.color}
          onChange={(color) => onChange(border({ color }))}
          colors={colors}
          autoLabel="Авто: линия"
        />
      ) : (
        <FieldHint className="mt-0">Цвет рамки — при толщине от 1 px.</FieldHint>
      )}
      <NumberSliderField
        label={`Тень ${of}`}
        value={cards.shadow}
        inherit={{ value: 0, label: "без тени" }}
        min={CS.min}
        max={CS.max}
        step={1}
        unit="%"
        presets={CARD_SHADOW_PRESETS}
        hint="50 — прежняя тень «С тенью», 100 — заметно плотнее."
        onChange={(v) => onChange({ ...cards, shadow: v ?? 0 })}
        onPreview={onPreview ? (v) => onPreview({ ...cards, shadow: v ?? 0 }) : undefined}
      />
    </div>
  );
}
